import React, { useState } from 'react';
import {Platform, View} from 'react-native';
import DropDownPicker from 'react-native-dropdown-picker';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {Formik, FormikHelpers} from 'formik';
import {observer} from 'mobx-react-lite';
import * as Yup from 'yup';

import AskToVerify from '../../components/rewards/AskToVerify';
import BackButton from '../../components/rewards/BackButton';
import CheckBox from '../../components/rewards/CheckBox';
import RewardsProgramImage from '../../components/rewards/ProgramImage';
import Button from '../../components/themed/Button';
import DateOfBirthInput from '../../components/themed/DateOfBirthInput';
import Text from '../../components/themed/Text';
import TextInput from '../../components/themed/TextInput';
import ContentView from '../../components/ui-controls/layout/ContentView';
import useSuburbs, {GeocodedSuburbExtended} from '../../hooks/useSuburbs';
import {AppRoute} from '../../models/AppRoute';
import {
  InformationContentType,
  RouteNavigatorScreenProps,
} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import apiService from '../../transport/apiService';
import {formatDateForZen} from '../../utils/date';
import {rewardsMarketingName} from '../../utils/rewards';

import {styles} from './styles';
import {useRewardsTermsAndConditionsPopper} from './TermsAndConditions';
import {SignUpSchema} from './validationSchema';

const RewardsSignUp = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.REWARDS_SIGN_UP>) => {

    const {rewardsStore} = useStores();
    const [isSuburbsOpen, setIsSuburbsOpen] = useState(false);
    const [selectedSuburbValue, setSelectedSuburbValue] = useState<string | null>(null);
    const suburbs = useSuburbs();

    console.debug('selectedSuburb', selectedSuburbValue);

    const {dialogNode, textNode} = useRewardsTermsAndConditionsPopper({
      text: 'Terms and Conditions',
      contentType: InformationContentType.REWARDS_PROGRAM_TERMS_AND_CONDITIONS,
    });

    // redirect to sign in if phoneNumber is empty string
    // React.useEffect(() => {
    //   if (rewardsStore.phoneNumber === '') {
    //     navigation.navigate(AppRoute.REWARDS_SIGN_IN);
    //   }
    // }, [navigation, rewardsStore.phoneNumber]);

    const validationSchema = SignUpSchema;

    type SignUpValues = Yup.InferType<typeof validationSchema>;

    const defaultValues: SignUpValues = {
      firstName: '',
      lastName: '',
      email: '',
      dateOfBirth: undefined,
      phoneNumber: rewardsStore.phoneNumber,
      confirmCheckbox: false,
      suburb: null,
    };

    const onSubmit = async (
      values: SignUpValues,
      {setSubmitting, setFieldError}: FormikHelpers<SignUpValues>,
    ) => {
      setSubmitting(true);
      try {
        const zedDOB = formatDateForZen(values.dateOfBirth);

        rewardsStore.setFirstName(values.firstName);
        rewardsStore.setLastName(values.lastName);
        rewardsStore.setEmailAddress(values.email);
        rewardsStore.setDateOfBirth(zedDOB);
        rewardsStore.setSuburb({
          locality: values.suburb.locality || '',
          postal_code: values.suburb.postal_code || '',
          administrative_area: values.suburb.administrative_area || '',
        });

        if (!rewardsStore?.phoneNumber) {
          console.error('Phone number is required');
        } else {
          const result = await apiService.rewardsVerifyNewUserPhoneNumber({
            phoneNumber: rewardsStore.phoneNumber,
            emailAddress: values.email,
          });

          if (result?.data) {
            if (result.data.emailAlreadyExists) {
              setFieldError(
                'email',
                'Email address already in use. Please use a different email address or contact support',
              );
            } else {
              // {"status":"pending"} is the typical response
              navigation.navigate(AppRoute.REWARDS_VERIFICATION);
            }
          }
          // TODO else we have some other error, and need to return a generic error message
        }
      } catch (e) {
        console.error('Error in /send-verification-new-lite-user:', e);
      }
      setSubmitting(false);
    };

    return (
      <KeyboardAwareScrollView keyboardShouldPersistTaps="always">
        <Formik
          initialValues={defaultValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}>
          {({
            errors,
            handleBlur,
            handleChange,
            initialValues,
            setFieldValue,
            setFieldError,
            setFieldTouched,
            touched,
            values,
            handleSubmit,
            isSubmitting,
          }) => {
            const handleSubmitPress = () => {
              handleSubmit();
            };

            const renderErrorMessageWhenTouchedOrPlaceholder = (
              name: keyof SignUpValues,
            ) => {
              console.debug(name, errors);
              return (
              <Text style={styles.errorMessage} id={`${name}-error`}>
                  {touched[name] && errors[name] ? (errors[name] as string) : ' '}
                </Text>
              );
            };

            return (
              <ContentView>
                <BackButton
                  onPress={() => {
                    navigation.navigate(AppRoute.REWARDS_SIGN_IN);
                  }}
                />
                <RewardsProgramImage variant="onLight" />
                <Text variant="heading1">
                  SIGN UP FOR {rewardsMarketingName.toUpperCase()}
                </Text>
                <Text>
                  Enter your details below for great savings and member only
                  prices!
                </Text>
                <View style={{gap: 8, zIndex: 1000, marginTop: 30}}>
                  <TextInput
                    accessibilityLabel="First name"
                    autoCapitalize="words"
                    autoComplete="given-name"
                    errorMessageWhenTouched={errors.firstName}
                    keyboardType="default"
                    label="First name"
                    maxLength={50}
                    onBlur={handleBlur('firstName')}
                    onChangeText={handleChange('firstName')}
                    touched={touched.firstName}
                    value={values.firstName}
                  />
                  <TextInput
                    accessibilityLabel="Last name"
                    autoCapitalize="words"
                    autoComplete="family-name"
                    errorMessageWhenTouched={errors.lastName}
                    keyboardType="default"
                    label="Last name"
                    maxLength={50}
                    onBlur={handleBlur('lastName')}
                    onChangeText={handleChange('lastName')}
                    touched={touched.lastName}
                    value={values.lastName}
                  />

                  <TextInput
                    accessibilityLabel="Email address"
                    autoCapitalize="none"
                    autoComplete="email"
                    errorMessageWhenTouched={errors.email}
                    keyboardType="email-address"
                    label="Email address"
                    maxLength={100}
                    onBlur={handleBlur('email')}
                    onChangeText={handleChange('email')}
                    touched={touched.email}
                  />

                  <View style={styles.dateOfBirthContainer}>
                    <Text variant="body2" style={{marginBottom: 8}}>
                      Date of Birth
                    </Text>
                    <DateOfBirthInput
                      initialDate={initialValues.dateOfBirth}
                      onChange={date => {
                        setFieldValue('dateOfBirth', date, false);
                        setFieldError('dateOfBirth', undefined);
                        setFieldTouched('dateOfBirth', true, false);
                      }}
                      onInvalidDate={date => {
                        setFieldValue('dateOfBirth', date, false);
                        setFieldError('dateOfBirth', 'Invalid date');
                        setFieldTouched('dateOfBirth', true, false);
                      }}
                    />
                    {renderErrorMessageWhenTouchedOrPlaceholder('dateOfBirth')}
                  </View>

                  {/* Dynamic z-index here is required due to DOB dropdown z-index issues */}
                  <View style={{zIndex: isSuburbsOpen ? 1000 : 999}}>
                    <Text variant="body2" style={{marginBottom: 8}}>
                      Postcode
                    </Text>
                    {/* @ts-ignore */}
                    <DropDownPicker<GeocodedSuburbExtended>
                      schema={{
                        label: 'label',
                        value: 'id',
                      }}
                      itemKey='id'
                      value={selectedSuburbValue}
                      setValue={setSelectedSuburbValue}
                      items={suburbs.list}
                      listMode={Platform.select({
                        native: 'MODAL',
                        default: 'SCROLLVIEW',
                      })}
                      disableLocalSearch={true}
                      searchable={true}
                      loading={suburbs.isLoading}
                      onChangeSearchText={suburbs.search}
                      onSelectItem={(item: GeocodedSuburbExtended) => setFieldValue('suburb', item)}
                      multiple={false}
                      open={isSuburbsOpen}
                      setOpen={setIsSuburbsOpen}
                      placeholder=""
                      searchPlaceholder="Enter your suburb or postcode"
                      itemSeparator={true}
                      searchTextInputProps={{
                        autoComplete: 'off',
                        autoCorrect: false,
                        autoFocus: !selectedSuburbValue,
                      }}
                      style={[styles.suburbsPicker,{
                        display: isSuburbsOpen ? 'none' : 'flex',
                      }]}
                      dropDownContainerStyle={styles.suburbsDropDownContainerStyle}
                      searchContainerStyle={styles.suburbsSearchContainerStyle}
                      searchTextInputStyle={styles.suburbsSearchTextInputStyle}
                      containerStyle={styles.suburbsContainerStyle}
                      itemSeparatorStyle={styles.suburbsItemSeparatorStyle}
                    />
                    {renderErrorMessageWhenTouchedOrPlaceholder('suburb')}
                  </View>

                  <View>
                    <TextInput
                      accessibilityLabel="Mobile number"
                      autoCapitalize="none"
                      keyboardType="phone-pad"
                      label="Mobile number"
                      maxLength={20}
                      // have to use rewardStore.phoneNumber
                      // initialValues.phoneNumber might not be up to date (tldr mobx)
                      value={rewardsStore.phoneNumber}
                      disabled
                    />

                    <AskToVerify />
                  </View>
                </View>

                <View style={{marginTop: 30}}>
                  <CheckBox
                    text={
                      (
                        <>
                          <Text variant="body2">
                            When signing up to the {rewardsMarketingName}{' '}
                            program, you agree with the {textNode}
                          </Text>
                        </>
                      ) as any
                    }
                    checked={values.confirmCheckbox}
                    onChange={() => {
                      setFieldValue('confirmCheckbox', !values.confirmCheckbox);
                    }}
                  />
                  {renderErrorMessageWhenTouchedOrPlaceholder(
                    'confirmCheckbox',
                  )}
                </View>
                {dialogNode}

                <View style={{alignItems: 'flex-end', marginTop: 16}}>
                  <Button
                    width={194}
                    title={'CONTINUE'}
                    onPress={handleSubmitPress}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                  />
                </View>
              </ContentView>
            );
          }}
        </Formik>
      </KeyboardAwareScrollView>
    );
  },
);

export default RewardsSignUp;
